import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Form, Input, Segmented, Select, Tooltip } from "antd";
import classNames from "classnames";

import styles from "./index.less";

import { RenderInputProps } from "../types";
import { PlusOutlined } from "@ant-design/icons";
import { useFlow } from "../../../FlowEdit/useFlow";
import {
  FlowNodeInputTypeEnum,
  FlowNodeTypeEnum,
} from "@packages/flow-core/module/node/constants";
import { ReactComponent as DeleteIcon } from "../../../../../assets/delete.svg";
import { ReactComponent as ArrowDownIcon } from "../../../../../assets/arrow-down.svg";
import { ReactComponent as ArrowUpIcon } from "../../../../../assets/arrow-up.svg";
import { VariableItemType } from "@packages/flow-core/module";

import PromptEditor from "../../../Editor/PromptEditor";

enum ConditionCompareEnum {
  isEmpty = "isEmpty",
  notEmpty = "notEmpty",
  startWith = "startWith",
  endWith = "endWith",
  contain = "contain",
  notContain = "notContain",
  eq = "eq",
  ne = "ne",
  gte = "gte",
  lte = "lte",
  gt = "gt",
  lt = "lt",
  pattern = "pattern",
  notPattern = "notPattern",
}

const compareOptions: { label: string; value: `${ConditionCompareEnum}` }[] = [
  {
    label: "为空",
    value: ConditionCompareEnum.isEmpty,
  },
  {
    label: "不为空",
    value: ConditionCompareEnum.notEmpty,
  },
  {
    label: "开始为",
    value: ConditionCompareEnum.startWith,
  },
  {
    label: "结束为",
    value: ConditionCompareEnum.endWith,
  },
  {
    label: "包含",
    value: ConditionCompareEnum.contain,
  },
  {
    label: "不包含",
    value: ConditionCompareEnum.notContain,
  },
  {
    label: "等于",
    value: ConditionCompareEnum.eq,
  },
  {
    label: "不等于",
    value: ConditionCompareEnum.ne,
  },
  {
    label: "大于等于",
    value: ConditionCompareEnum.gte,
  },
  {
    label: "小于等于",
    value: ConditionCompareEnum.lte,
  },
  {
    label: "大于",
    value: ConditionCompareEnum.gt,
  },
  {
    label: "小于",
    value: ConditionCompareEnum.lt,
  },
  {
    label: "正则匹配",
    value: ConditionCompareEnum.pattern,
  },
  {
    label: "正则不匹配",
    value: ConditionCompareEnum.notPattern,
  },
];

export default function ConditionInput({
  item,
  moduleId,
  inputs,
}: RenderInputProps) {
  const [form] = Form.useForm();
  const compareValue = Form.useWatch("compare", form);

  const {
    addModuleInput,
    addModuleOutput,
    updateNodeValue,
    updateModule,
    getAllNodes,
    systemVariables = [],
  } = useFlow();

  const [isFolding, setIsFolding] = useState<boolean>(false);

  const [caseNumber, conditionNumber, isFirstCondition] = useMemo(() => {
    const regexpResult = /case\-([0-9]+)\-condition\-([0-9]+)/.exec(item.key)!;
    return [
      Number(regexpResult[1]),
      Number(regexpResult[2]),
      regexpResult[2] === "0",
    ];
  }, [JSON.stringify(item)]);

  const currentNodeData = useMemo(
    () => getAllNodes().find((node) => node.data.moduleId === moduleId)!.data,
    [getAllNodes, JSON.stringify(inputs)]
  );

  const currentRelationship = useMemo(() => {
    return (
      currentNodeData.inputs.find(
        (input) => input.key === `case-${caseNumber}-relationship`
      )?.value ?? "AND"
    );
  }, [caseNumber, currentNodeData]);

  useEffect(() => {
    let rawValue = item.value;
    // 后端不支持模板是 string array 的结构，自己解出来
    if (!Array.isArray(item.value)) {
      rawValue = JSON.parse(item.value as string);
    }
    const [value, compare, comparativeValue] = (rawValue as string[]) ?? [];
    form.setFieldsValue({ value, compare, comparativeValue });
  }, [JSON.stringify(item)]);

  useEffect(() => {
    const doms =
      document.getElementsByClassName(`case-${caseNumber}-condition`) ?? [];
    if (isFolding) {
      [...doms].forEach((d) => d.setAttribute("style", "display: none;"));
    } else {
      [...doms].forEach((d) => d.removeAttribute("style"));
    }
  }, [isFolding, caseNumber]);

  const caseTotalNumber = useMemo(
    () =>
      Math.max(
        ...(inputs
          ?.map((input) => /case\-([0-9]+)/.exec(input.key)?.[1])
          ?.filter((v) => v !== void 0)
          ?.map((v) => Number(v)) ?? [0])
      ),
    [JSON.stringify(inputs)]
  );

  const currentCaseConditions = useMemo(
    () =>
      inputs?.filter((input) =>
        input.key.startsWith(`case-${caseNumber}-condition-`)
      ) ?? [],
    [caseNumber, JSON.stringify(inputs)]
  );

  const isLastCondition = useMemo(
    () =>
      Number(/case\-[0-9]+\-condition\-([0-9]+)/.exec(item.key)![1]) ===
      currentCaseConditions.length - 1,
    [currentCaseConditions]
  );

  const addNewCaseCondition = useCallback(() => {
    const key = `case-${caseNumber}-condition-${currentCaseConditions.length}`;
    addModuleInput(moduleId, {
      key,
      type: FlowNodeInputTypeEnum.conditionInput,
      label: "",
      value: ["", ConditionCompareEnum.isEmpty, ""],
      edit: true,
    });

    if (currentCaseConditions.length === 1) {
      addModuleInput(moduleId, {
        key: `case-${caseNumber}-relationship`,
        type: FlowNodeInputTypeEnum.hidden,
        label: "",
        value: "AND", // 默认为 and
        edit: true,
      });
    }
  }, [caseNumber, currentCaseConditions]);

  const addNewCase = useCallback(() => {
    const key = `case-${caseTotalNumber + 1}-condition-0`;
    addModuleInput(moduleId, {
      key,
      type: FlowNodeInputTypeEnum.conditionInput,
      label: "",
      value: ["", ConditionCompareEnum.isEmpty, ""],
      edit: true,
    });
    addModuleOutput(moduleId, {
      key: `if-case-${caseTotalNumber + 1}`,
      label: `满足判断 ${caseTotalNumber + 2}`,
      valueType: "bool",
      type: "source",
      targets: [],
      edit: true,
    });
  }, [moduleId]);

  const variables = useMemo(() => {
    const templteVariables =
      getAllNodes()
        .find((node) => node.data.flowType === FlowNodeTypeEnum.templateParam)
        ?.data?.inputs.map((item) => ({
          key: item.key,
          label: item.label,
          isTemplateVariable: true,
        })) || [];
    const globalVariablesModule: Pick<VariableItemType, "key" | "label">[] =
      getAllNodes()
        .find((node) => node.data.flowType === FlowNodeTypeEnum.globalVariable)
        ?.data?.inputs.filter((input) => input.edit)
        .map((item) => ({ key: item.key, label: item.label })) || [];

    const moduleVariablesModule: Pick<VariableItemType, "key" | "label">[] =
      inputs
        ?.filter((input) => input.type === FlowNodeInputTypeEnum.target)
        .map((item) => ({
          key: item.key,
          label: item.description || item.label,
          self: true,
        })) || [];

    return [
      ...templteVariables,
      ...globalVariablesModule,
      ...moduleVariablesModule,
      ...systemVariables,
    ];
  }, [getAllNodes, systemVariables, inputs]);

  return (
    <div className={styles["condition-container"]}>
      {isFirstCondition && (
        <div className={styles.case}>
          <div className={styles.left}>
            <div className={styles.title}>判断 {caseNumber + 1}</div>
            {caseTotalNumber > 0 && (
              <Tooltip title={`删除判断 ${caseNumber + 1}`}>
                <DeleteIcon
                  style={{
                    color: "#8F959E",
                  }}
                  onClick={() => {
                    const toUpdateInputs = currentNodeData.inputs
                      .filter(
                        (input) => !input.key.startsWith(`case-${caseNumber}-`)
                      )
                      .map((input) => {
                        const regexpResult = /case\-([0-9]+)\-/.exec(input.key);
                        if (!regexpResult) {
                          return input;
                        }

                        const currentCaseNumber = Number(regexpResult[1]);
                        if (currentCaseNumber > caseNumber) {
                          return {
                            ...input,
                            key: input.key.replace(
                              /case\-[0-9]+/,
                              `case-${currentCaseNumber - 1}`
                            ),
                          };
                        }

                        return input;
                      });

                    const toUpdateOutputs = currentNodeData.outputs
                      .filter(
                        (output) => output.key !== `if-case-${caseNumber}`
                      )
                      .map((output) => {
                        if (!output.key.startsWith("if-case-")) {
                          return output;
                        }

                        const currentCaseNumber = Number(
                          /if\-case\-([0-9]+)/.exec(output.key)![1]
                        );
                        if (currentCaseNumber > caseNumber) {
                          return {
                            ...output,
                            key: `if-case-${currentCaseNumber - 1}`,
                            label: `满足判断 ${currentCaseNumber}`,
                          };
                        }

                        return output;
                      });

                    updateModule(moduleId, {
                      ...currentNodeData,
                      inputs: toUpdateInputs,
                      outputs: toUpdateOutputs,
                    });
                  }}
                />
              </Tooltip>
            )}

            {currentCaseConditions?.length > 1 && (
              <Segmented
                size="small"
                options={["AND", "OR"]}
                defaultValue={currentRelationship}
                className={styles.relationship}
                onChange={(v) => {
                  // 更新条件关系
                  updateNodeValue(
                    moduleId,
                    `case-${caseNumber}-relationship`,
                    v
                  );
                }}
              />
            )}
          </div>

          <Tooltip title={isFolding ? "展开判断" : "折叠判断"}>
            {isFolding ? (
              <ArrowDownIcon
                className={styles.fold}
                onClick={() => setIsFolding(false)}
              />
            ) : (
              <ArrowUpIcon
                className={styles.fold}
                onClick={() => setIsFolding(true)}
              />
            )}
          </Tooltip>
        </div>
      )}
      <Form
        form={form}
        className={classNames(styles.condition, `case-${caseNumber}-condition`)}
        layout="horizontal"
        onValuesChange={async (_, values) => {
          const { value, compare, comparativeValue } = values;
          const inputValue = [
            value ?? "",
            compare,
            compare === ConditionCompareEnum.isEmpty ||
            compare === ConditionCompareEnum.notEmpty
              ? ""
              : comparativeValue ?? "",
          ];
          updateNodeValue(moduleId, item.key, inputValue);
        }}
      >
        <Form.Item
          name="value"
          className={classNames(styles["form-item"], styles["condition-value"])}
        >
          <PromptEditor
            h={32}
            variables={variables}
            singleLine
            showOpenModal={false}
            showResize={false}
          />
        </Form.Item>
        <Form.Item
          name="compare"
          className={classNames(
            styles["form-item"],
            styles["condition-compare"]
          )}
          rules={[
            {
              required: true,
            },
          ]}
        >
          <Select placeholder="选择条件" options={compareOptions} />
        </Form.Item>
        <Form.Item
          name="comparativeValue"
          className={classNames(
            styles["form-item"],
            styles["condition-comparative-value"]
          )}
        >
          {!(
            compareValue === ConditionCompareEnum.isEmpty ||
            compareValue === ConditionCompareEnum.notEmpty
          ) && (
            <PromptEditor
              h={32}
              variables={variables}
              singleLine
              showOpenModal={false}
              showResize={false}
            />
          )}
          {(compareValue === ConditionCompareEnum.isEmpty ||
            compareValue === ConditionCompareEnum.notEmpty) && (
            <Input disabled />
          )}
        </Form.Item>

        {currentCaseConditions.length > 1 && (
          <Button
            type="link"
            size="small"
            className={styles["condition-delete"]}
            onClick={() => {
              let filteredInputs = currentNodeData.inputs.filter(
                (input) => input.key !== item.key
              );

              const conditions = filteredInputs.filter((input) =>
                input.key.startsWith(`case-${caseNumber}-condition-`)
              );

              if (conditions.length === 1) {
                filteredInputs = filteredInputs.filter(
                  (input) => input.key !== `case-${caseNumber}-relationship`
                );
              }

              const toUpdateInputs = filteredInputs.map((input) => {
                const regexpResult = new RegExp(
                  `case\\-${caseNumber}\\-condition\\-([0-9]+)`
                ).exec(input.key);
                if (!regexpResult) {
                  return input;
                }

                const currentConditionNumber = Number(regexpResult[1]);
                if (currentConditionNumber > conditionNumber) {
                  return {
                    ...input,
                    key: input.key.replace(
                      /condition\-[0-9]+/,
                      `condition-${currentConditionNumber - 1}`
                    ),
                  };
                }

                return input;
              });

              updateModule(moduleId, {
                ...currentNodeData,
                inputs: toUpdateInputs,
              });
            }}
          >
            删除
          </Button>
        )}
      </Form>
      {isLastCondition && (
        <Button
          icon={<PlusOutlined />}
          onClick={addNewCaseCondition}
          className={classNames(
            styles["add-new-condition"],
            `case-${caseNumber}-condition`
          )}
        >
          添加条件
        </Button>
      )}
      {caseNumber === caseTotalNumber && isLastCondition && (
        <Button
          type="dashed"
          icon={<PlusOutlined />}
          onClick={addNewCase}
          className={styles["add-new-case"]}
        >
          新增判断
        </Button>
      )}
    </div>
  );
}
